// ============================================
// AVISONUEVAVERSION.JS — "hay una version nueva, recarga"
// ============================================
// POR QUE EXISTE ASI (AVISO-VERSION, 2026-09-18)
// El aviso YA FUNCIONABA, pero repartido en tres sitios y ninguno facil de encontrar:
//   1. `index.js` pintaba un banner con `document.createElement` a pelo (el que se veia de verdad)
//   2. `components/ActualizadorPWA.js` — muerto, y ademas registraba OTRO service worker (`/sw.js`)
//   3. `components/UpdateBanner.js` — muerto
// Los dos muertos se habian retirado a proposito en mayo (commit 83d4e55), pero los archivos se
// quedaron en el repo. Resultado: tres implementaciones de una sola cosa, dos de ellas trampa, y
// la unica viva escondida fuera del arbol de React donde nadie la encuentra buscando. Por eso
// parecia que "quedo a medias" cuando en realidad funcionaba.
//
// Este componente es ahora el UNICO. El registro del service worker sigue en `index.js` (tiene que
// correr antes que React), pero en vez de pintar el banner a mano avisa por un evento de ventana y
// aqui se pinta con React, donde se puede ver, buscar y mantener.
//
// COMO LLEGA EL AVISO
// `serviceWorkerRegistration.register({ onUpdate })` comprueba si hay version nueva al cargar, al
// volver a la pestaña, al recuperar la conexion, al enfocar la ventana y CADA 60 SEGUNDOS. Cuando
// la encuentra, index.js llama a `anunciarNuevaVersion(registration)`. Comprobado ejecutando con un
// despliegue simulado: con la pestaña abierta y sin tocar nada, el aviso aparece solo.
//
// POSPONER, NO ESCONDER
// Se puede posponer, porque a veces no es momento de recargar (estas a medio escribir algo). Pero
// vuelve a los 30 minutos: el objetivo es que nadie pase el dia entero con una version vieja, que
// es justo lo que pasaba. El aplazamiento vive en sessionStorage, asi que al abrir de nuevo la app
// el aviso vuelve.

import React, { useEffect, useState, useCallback } from 'react';
import useResponsive from '../hooks/useResponsive';

const PP = "'Poppins', sans-serif";
const AZUL = '#265F97';
const EVENTO = 'beep:nueva-version';
const CLAVE_POSPUESTO = 'beep_version_pospuesta_hasta';
const POSPONER_MS = 30 * 60 * 1000;

// El registro puede llegar ANTES de que React monte el componente (el service worker se comprueba
// nada mas cargar). Se guarda aqui para no perder el aviso por una carrera de arranque.
let registroPendiente = null;

/** La llama `index.js` desde el onUpdate del service worker. */
export function anunciarNuevaVersion(registration) {
  registroPendiente = registration || null;
  try {
    window.dispatchEvent(new CustomEvent(EVENTO, { detail: registration }));
  } catch (e) { /* un aviso nunca debe romper la app */ }
}

function pospuestoAhora() {
  try {
    const hasta = Number(sessionStorage.getItem(CLAVE_POSPUESTO) || 0);
    return hasta > Date.now();
  } catch (e) { return false; }   // modo incognito / almacenamiento bloqueado
}

export default function AvisoNuevaVersion() {
  const [registro, setRegistro] = useState(registroPendiente);
  const [oculto, setOculto] = useState(pospuestoAhora);
  const [recargando, setRecargando] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    const alAvisar = (e) => { setRegistro(e.detail || registroPendiente); setOculto(pospuestoAhora()); };
    window.addEventListener(EVENTO, alAvisar);
    // Por si el aviso se disparo antes de montar.
    if (registroPendiente) setRegistro(registroPendiente);
    return () => window.removeEventListener(EVENTO, alAvisar);
  }, []);

  // Mientras este pospuesto, se revisa cada minuto si ya toca volver a enseñarlo.
  useEffect(() => {
    if (!oculto) return undefined;
    const t = setInterval(() => { if (!pospuestoAhora()) setOculto(false); }, 60000);
    return () => clearInterval(t);
  }, [oculto]);

  const recargar = useCallback(() => {
    setRecargando(true);
    try {
      if (registro && registro.waiting) registro.waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch (e) { /* si falla, la recarga de abajo arregla igual */ }
    setTimeout(() => window.location.reload(), 300);
  }, [registro]);

  const posponer = useCallback(() => {
    try { sessionStorage.setItem(CLAVE_POSPUESTO, String(Date.now() + POSPONER_MS)); } catch (e) {}
    setOculto(true);
  }, []);

  if (!registro || oculto) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        zIndex: 10000,
        fontFamily: PP,
        // En celular ocupa el ancho con margenes y se separa del borde inferior con el hueco
        // seguro del telefono (la barra de gestos de iOS/Android se come los ultimos pixeles).
        // En pantalla grande se queda en la esquina, sin tapar el contenido.
        ...(isMobile
          ? { left: 12, right: 12, bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))' }
          : { right: 20, bottom: 20, maxWidth: 420 }),
        background: AZUL,
        color: '#fff',
        borderRadius: 12,
        boxShadow: '0 6px 24px rgba(0,0,0,0.24)',
        padding: isMobile ? '12px 14px' : '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Hay una versión nueva</div>
        <div style={{ fontSize: 11, opacity: 0.9 }}>
          Recarga para trabajar con los últimos cambios.
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={posponer}
          disabled={recargando}
          style={{
            background: 'transparent', color: '#fff',
            border: '1px solid rgba(255,255,255,0.55)', borderRadius: 8,
            padding: '8px 12px', minHeight: 40, fontSize: 12, fontFamily: PP,
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          Ahora no
        </button>
        <button
          onClick={recargar}
          disabled={recargando}
          style={{
            background: '#fff', color: AZUL, border: 'none', borderRadius: 8,
            padding: '8px 14px', minHeight: 40, fontWeight: 700, fontSize: 12,
            fontFamily: PP, cursor: recargando ? 'default' : 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {recargando ? 'Recargando…' : 'Recargar'}
        </button>
      </div>
    </div>
  );
}
